import { useEffect, useMemo } from 'react';

import { isEmpty } from '@virtuslab/nfs-shared/src/services/checks';
import useLocalStorage from '@virtuslab/nfs-shared/src/services/hooks/useLocalStorage';
import type { NonMutable, Nullable } from '@virtuslab/nfs-shared/src/services/object';

import { localStorageKeys } from '../../config/component';

type UseLastContractIndexParams = Readonly<{
  contracts: NonMutable<Readonly<{ id: string }>[]>;
  urlContractIndex: Nullable<string>;
}>;

const resolveContractIndex = (
  contracts: NonMutable<Readonly<{ id: string }>[]>,
  storedContractId: Nullable<string>,
): number => {
  if (!isEmpty(storedContractId)) {
    const index = contracts.findIndex((c) => c.id === storedContractId);
    if (index >= 0) {
      return index;
    }
  }
  return 0;
};

const useLastContractIndex = ({
  contracts,
  urlContractIndex,
}: UseLastContractIndexParams): number => {
  const [storedContractId, setStoredContractId] = useLocalStorage<string>(
    localStorageKeys.lastSelectedContractId, '',
  );

  const { contractIndex, contractId } = useMemo(() => {
    if (!isEmpty(urlContractIndex)) {
      const idx = Number(urlContractIndex);
      if (Number.isInteger(idx) && idx >= 0 && idx < contracts.length) {
        return { contractIndex: idx, contractId: contracts[idx].id };
      }
    }
    const idx = resolveContractIndex(contracts, storedContractId);
    return { contractIndex: idx, contractId: contracts[idx]?.id };
  }, [urlContractIndex, contracts, storedContractId]);

  useEffect(() => {
    if (!isEmpty(contractId)) {
      setStoredContractId(contractId);
    }
  }, [contractId, setStoredContractId]);

  return contractIndex;
};

export default useLastContractIndex;
